import apiClient from "@/lib/axios-client"
import { API_ENDPOINTS } from "@/lib/api-endpoints"
import type { UserPreferences } from "@/contexts/preferences-context"

export interface ChangePasswordRequest {
    currentPassword: string
    newPassword: string
    confirmPassword: string
}

export interface UpdateProfileRequest {
    firstName?: string
    lastName?: string
    email?: string
    currentPassword?: string
}

export interface Enable2FARequest {
    enabled: boolean
}

export interface UpdateSettingsRequest {
    emailNotifications?: boolean
    pushNotifications?: boolean
    smsNotifications?: boolean
    newOrdersNotification?: boolean
    statusUpdatesNotification?: boolean
    weeklyReportNotification?: boolean
}

export interface UpdatePreferencesRequest {
    theme?: "light" | "dark" | "system"
    language?: "ro" | "en" | "de"
    accentColor?: "emerald" | "blue" | "violet" | "amber" | "rose"
    timezone?: string
    currency?: string
}

export interface Session {
    id: string
    device: string
    ip: string
    location: string
    isCurrent: boolean
    lastActivity: string
}

export interface ApiResponse<T> {
    success: boolean
    data?: T
    message?: string
}

export interface CompanyMemberSummary {
    userId: number
    email: string
    firstName?: string | null
    lastName?: string | null
    role: string
    enabled: boolean
    companyMaster: boolean
    createdAt?: string | null
}

export interface ApprovedCompanyUserEmail {
    id: number
    email: string
    approvedByUserId?: number | null
    approvedByName?: string | null
    createdAt?: string | null
    invitationSentAt?: string | null
    invitationExpiresAt?: string | null
}

export interface CompanyUserAccess {
    companyId: number
    companyName: string
    allowAdditionalUsers: boolean
    canManageAccess: boolean
    members: CompanyMemberSummary[]
    approvedEmails: ApprovedCompanyUserEmail[]
}

export interface ExchangeRateReference {
    baseCurrency: string
    quoteCurrency: string
    rate: number
    rateDate?: string
    fetchedAt?: string
    source?: string
}

type UnknownRecord = Record<string, unknown>

const asRecord = (value: unknown): UnknownRecord => {
    return value && typeof value === "object" ? (value as UnknownRecord) : {}
}

const extractErrorMessage = (error: unknown, fallback: string): string => {
    const root = asRecord(error)
    const response = asRecord(root.response)
    const data = asRecord(response.data)
    if (typeof data.message === "string" && data.message.trim()) return data.message
    if (typeof root.message === "string" && root.message.trim()) return root.message
    return fallback
}

const parseBoolean = (value: unknown): boolean => {
    if (typeof value === "boolean") return value
    if (typeof value === "string") return value.toLowerCase() === "true"
    if (typeof value === "number") return value === 1
    return false
}

const normalizeText = (value: unknown, fallback: string): string => {
    if (typeof value === "string" && value.trim().length > 0) return value.trim()
    return fallback
}

const normalizeTheme = (value: unknown): UserPreferences["theme"] | undefined => {
    if (typeof value !== "string") return undefined
    const normalized = value.trim().toLowerCase()
    if (normalized === "light" || normalized === "dark" || normalized === "system") {
        return normalized
    }
    return undefined
}

const normalizeLanguage = (value: unknown): UserPreferences["language"] | undefined => {
    if (typeof value !== "string") return undefined
    const normalized = value.trim().toLowerCase()
    if (normalized === "ro" || normalized === "en" || normalized === "de") {
        return normalized
    }
    return undefined
}

const normalizeAccentColor = (value: unknown): UserPreferences["accentColor"] | undefined => {
    if (typeof value !== "string") return undefined
    const normalized = value.trim().toLowerCase()
    if (normalized === "emerald" || normalized === "blue" || normalized === "violet" || normalized === "amber" || normalized === "rose") {
        return normalized
    }
    return undefined
}

const toTimestamp = (value: unknown): number => {
    if (typeof value === "number" && Number.isFinite(value)) return value
    if (typeof value === "string" && value.trim()) {
        const parsed = Date.parse(value)
        if (!Number.isNaN(parsed)) return parsed
    }
    return 0
}

const parseBrowserName = (ua: string): string => {
    if (!ua || ua === "Unknown device") return ua

    // Order matters: check specific browsers before generic ones
    const patterns: [RegExp, string][] = [
        [/Edg(?:e|A)?\/(\d+)/, "Edge"],
        [/OPR\/(\d+)/, "Opera"],
        [/Vivaldi\/(\d+)/, "Vivaldi"],
        [/Brave\/(\d+)/, "Brave"],
        [/SamsungBrowser\/(\d+)/, "Samsung Browser"],
        [/Firefox\/(\d+)/, "Firefox"],
        [/Chrome\/(\d+)/, "Chrome"],
        [/Version\/(\d+).*Safari/, "Safari"],
    ]

    for (const [regex, name] of patterns) {
        const match = ua.match(regex)
        if (match) return `${name} ${match[1]}`
    }

    return "Browser"
}

const parseOsName = (ua: string): string => {
    if (!ua) return ""
    if (ua.includes("Windows")) return "Windows"
    if (ua.includes("Mac OS") || ua.includes("Macintosh")) return "macOS"
    if (ua.includes("Android")) return "Android"
    if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS"
    if (ua.includes("Linux")) return "Linux"
    if (ua.includes("CrOS")) return "ChromeOS"
    return ""
}

const normalizeSession = (raw: unknown, index: number): Session => {
    const row = asRecord(raw)
    const id = String(row.id ?? row.sessionId ?? row._id ?? row.jti ?? `session-${index}`)
    const rawDevice = normalizeText(row.device ?? row.deviceName ?? row.browser ?? row.userAgent, "Unknown device")
    const browser = parseBrowserName(rawDevice)
    const os = parseOsName(rawDevice)
    const device = os ? `${browser} · ${os}` : browser
    const ip = normalizeText(row.ip ?? row.ipAddress, "Unknown IP")
    const location = normalizeText(row.location ?? row.geo ?? row.country, "Unknown location")
    const isCurrent = parseBoolean(row.isCurrent ?? row.current ?? row.currentSession)
    const lastActivity = normalizeText(row.lastActivity ?? row.updatedAt ?? row.createdAt, "")

    return {
        id,
        device,
        ip,
        location,
        isCurrent,
        lastActivity,
    }
}

const buildSessionFingerprint = (session: Session): string => {
    return `${session.device.toLowerCase()}|${session.ip.toLowerCase()}|${session.location.toLowerCase()}`
}

const normalizeAndDeduplicateSessions = (rawSessions: unknown[]): Session[] => {
    const dedupedMap = new Map<string, Session>()

    rawSessions.forEach((rawSession, index) => {
        const normalized = normalizeSession(rawSession, index)
        const fingerprint = buildSessionFingerprint(normalized)
        const existing = dedupedMap.get(fingerprint)

        if (!existing) {
            dedupedMap.set(fingerprint, normalized)
            return
        }

        if (normalized.isCurrent && !existing.isCurrent) {
            dedupedMap.set(fingerprint, normalized)
            return
        }

        if (normalized.isCurrent === existing.isCurrent) {
            const normalizedTs = toTimestamp(normalized.lastActivity)
            const existingTs = toTimestamp(existing.lastActivity)
            if (normalizedTs > existingTs) {
                dedupedMap.set(fingerprint, normalized)
            }
        }
    })

    return Array.from(dedupedMap.values()).sort((a, b) => {
        if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1
        return toTimestamp(b.lastActivity) - toTimestamp(a.lastActivity)
    })
}

class SettingsService {
    // --- PASSWORD MANAGEMENT ---
    async changePassword(data: ChangePasswordRequest): Promise<ApiResponse<void>> {
        try {
            const response = await apiClient.post(API_ENDPOINTS.CHANGE_PASSWORD, {
                currentPassword: data.currentPassword,
                newPassword: data.newPassword,
                confirmPassword: data.confirmPassword,
            })
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to change password"),
            }
        }
    }

    // --- PROFILE UPDATE ---
    async updateProfile(data: UpdateProfileRequest): Promise<ApiResponse<void>> {
        try {
            const payload: Record<string, string> = {}
            if (data.firstName !== undefined) payload.firstName = data.firstName
            if (data.lastName !== undefined) payload.lastName = data.lastName
            if (data.email !== undefined) payload.email = data.email

            const response = await apiClient.put(API_ENDPOINTS.UPDATE_PROFILE, payload)
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to update profile"),
            }
        }
    }

    // --- 2FA MANAGEMENT ---
    async enable2FA(): Promise<ApiResponse<{ secret?: string; otpauthUri?: string; backupCodes?: string[] }>> {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ENABLE_2FA)
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to start 2FA"),
            }
        }
    }

    async verify2FA(code: string): Promise<ApiResponse<void>> {
        try {
            const response = await apiClient.post(API_ENDPOINTS.VERIFY_2FA, { code })
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to verify 2FA"),
            }
        }
    }

    async disable2FA(password: string): Promise<ApiResponse<void>> {
        try {
            const response = await apiClient.post(API_ENDPOINTS.DISABLE_2FA, { password })
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to disable 2FA"),
            }
        }
    }

    // --- SESSION MANAGEMENT ---
    async getActiveSessions(): Promise<ApiResponse<Session[]>> {
        try {
            const response = await apiClient.get(API_ENDPOINTS.GET_SESSIONS)
            const rawSessions = response.data?.data || response.data?.sessions || []
            const normalizedSessions = Array.isArray(rawSessions) ? normalizeAndDeduplicateSessions(rawSessions) : []
            return {
                success: true,
                data: normalizedSessions,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to fetch sessions"),
            }
        }
    }

    async logoutSession(sessionId: string): Promise<ApiResponse<void>> {
        try {
            await apiClient.post(API_ENDPOINTS.LOGOUT_SESSION(sessionId))
            return {
                success: true,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to logout session"),
            }
        }
    }

    async terminateAllOtherSessions(): Promise<ApiResponse<void>> {
        try {
            await apiClient.post(API_ENDPOINTS.TERMINATE_ALL_SESSIONS)
            return {
                success: true,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to terminate sessions"),
            }
        }
    }

    // --- NOTIFICATION SETTINGS ---
    async updateNotificationSettings(settings: UpdateSettingsRequest): Promise<ApiResponse<void>> {
        try {
            const response = await apiClient.put(API_ENDPOINTS.UPDATE_SETTINGS, settings)
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to update notification settings"),
            }
        }
    }

    // --- USER PREFERENCES ---
    async getUserSettings(): Promise<ApiResponse<Partial<UserPreferences>>> {
        try {
            const response = await apiClient.get(API_ENDPOINTS.USER_SETTINGS)
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to fetch user settings"),
            }
        }
    }

    async getUserPreferences(): Promise<ApiResponse<Partial<UserPreferences>>> {
        try {
            const response = await apiClient.get(API_ENDPOINTS.UPDATE_PREFERENCES)
            const payload = asRecord(response.data?.data)
            const theme = normalizeTheme(payload.theme)
            const language = normalizeLanguage(payload.language)
            const accentColor = normalizeAccentColor(payload.accentColor)
            const timezone = typeof payload.timezone === "string" && payload.timezone.trim() ? payload.timezone.trim() : undefined
            const currency = typeof payload.currency === "string" && payload.currency.trim() ? payload.currency.trim() : undefined

            return {
                success: true,
                data: {
                    ...(theme ? { theme } : {}),
                    ...(language ? { language } : {}),
                    ...(accentColor ? { accentColor } : {}),
                    ...(timezone ? { timezone } : {}),
                    ...(currency ? { currency } : {}),
                },
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to fetch user preferences"),
            }
        }
    }

    async updateUserPreferences(prefs: UpdatePreferencesRequest): Promise<ApiResponse<void>> {
        try {
            const response = await apiClient.put(API_ENDPOINTS.UPDATE_PREFERENCES, prefs)
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to update preferences"),
            }
        }
    }

    async getLatestExchangeRate(base = "EUR", quote = "RON"): Promise<ApiResponse<ExchangeRateReference>> {
        try {
            const response = await apiClient.get(API_ENDPOINTS.EXCHANGE_RATES_LATEST, {
                params: {
                    base,
                    quote,
                },
            })

            const payload = asRecord(response.data?.data)
            const parsedRate = Number(payload.rate)

            return {
                success: true,
                data: {
                    baseCurrency: normalizeText(payload.baseCurrency, base),
                    quoteCurrency: normalizeText(payload.quoteCurrency, quote),
                    rate: Number.isFinite(parsedRate) ? parsedRate : 0,
                    rateDate: typeof payload.rateDate === "string" ? payload.rateDate : undefined,
                    fetchedAt: typeof payload.fetchedAt === "string" ? payload.fetchedAt : undefined,
                    source: typeof payload.source === "string" ? payload.source : undefined,
                },
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to fetch latest exchange rate"),
            }
        }
    }

    // --- COMPANY USER ACCESS ---
    async getCompanyUserAccess(): Promise<ApiResponse<CompanyUserAccess>> {
        try {
            const response = await apiClient.get(API_ENDPOINTS.COMPANY_USER_ACCESS)
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to fetch company user access settings"),
            }
        }
    }

    async updateCompanyUserAccessPolicy(allowAdditionalUsers: boolean): Promise<ApiResponse<CompanyUserAccess>> {
        try {
            const response = await apiClient.put(API_ENDPOINTS.COMPANY_USER_ACCESS_POLICY, {
                allowAdditionalUsers,
            })
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to update company user access policy"),
            }
        }
    }

    async addApprovedCompanyUserEmail(email: string): Promise<ApiResponse<CompanyUserAccess>> {
        try {
            const response = await apiClient.post(API_ENDPOINTS.COMPANY_USER_ALLOWLIST, { email })
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to add approved email"),
            }
        }
    }

    async removeApprovedCompanyUserEmail(approvedEmailId: number): Promise<ApiResponse<CompanyUserAccess>> {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.COMPANY_USER_ALLOWLIST_ITEM(approvedEmailId))
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to remove approved email"),
            }
        }
    }

    async resendApprovedCompanyUserInvitation(approvedEmailId: number): Promise<ApiResponse<CompanyUserAccess>> {
        try {
            const response = await apiClient.post(API_ENDPOINTS.COMPANY_USER_ALLOWLIST_RESEND(approvedEmailId))
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to resend invitation email"),
            }
        }
    }

    // --- COMBINED UPDATE (Recomended for batch updates) ---
    async updateAllSettings(
        notificationSettings: UpdateSettingsRequest,
        preferences: UpdatePreferencesRequest
    ): Promise<ApiResponse<void>> {
        try {
            const [notifResponse, prefResponse] = await Promise.all([
                this.updateNotificationSettings(notificationSettings),
                this.updateUserPreferences(preferences),
            ])

            if (!notifResponse.success || !prefResponse.success) {
                return {
                    success: false,
                    message: notifResponse.message || prefResponse.message || "Failed to update settings",
                }
            }

            return {
                success: true,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to update settings"),
            }
        }
    }

    // --- ACCOUNT DELETION ---
    async scheduleAccountDeletion(password: string): Promise<ApiResponse<void>> {
        try {
            const response = await apiClient.post(API_ENDPOINTS.SCHEDULE_ACCOUNT_DELETION, { password })
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to schedule account deletion"),
            }
        }
    }

    async deleteAccountImmediate(password: string): Promise<ApiResponse<void>> {
        try {
            const response = await apiClient.post(API_ENDPOINTS.DELETE_ACCOUNT_IMMEDIATE, { password })
            return {
                success: true,
                data: response.data?.data,
            }
        } catch (error: unknown) {
            return {
                success: false,
                message: extractErrorMessage(error, "Failed to delete account"),
            }
        }
    }
}

const settingsService = new SettingsService()

export default settingsService
